/*
 * @file dateWindow.js
 * Утиліти для визначення, який блок показувати зараз за датами.
 */

/*
 * @typedef {Object} DateWindow
 * @property {string}  key          Унікальний ключ (наприклад, 'halloween', 'winter')
 * @property {string|Date} start    Початок діапазону (включно). Підтримує 'DD.MM', 'YYYY-MM-DD' або Date.
 * @property {string|Date} end      Кінець діапазону (включно). Підтримує 'DD.MM', 'YYYY-MM-DD' або Date.
 * @property {boolean=} recurring   true => інтерпретувати як щорічне вікно ('DD.MM'). За замовчуванням true для 'DD.MM'.
 */

/*
 * Перевіряє, чи строка у форматі DD.MM
 * @param {string} s
 * @returns {boolean}
 */
function isDM(s) {
	return typeof s === 'string' && /^\d{2}\.\d{2}$/.test(s);
}

/*
 * Перевіряє, чи строка у форматі YYYY-MM-DD
 * @param {string} s
 * @returns {boolean}
 */
function isYMD(s) {
	return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

/*
 * Перетворює 'DD.MM' на число MMDD (для простих порівнянь у межах року).
 * @param {string} dm - 'DD.MM'
 * @returns {number} - наприклад, '25.10' -> 1025
 */
function dmToNumber(dm) {
	const [ d, m ] = dm.split('.').map(n => parseInt(n, 10));
	return (m * 100) + d;
}

/*
 * Перевіряє, чи поточна дата (now) входить у «щорічне» вікно 'DD.MM'..'DD.MM'.
 * Підтримує перетин року (наприклад, 01.12–10.01).
 *
 * @param {string} startDM - 'DD.MM'
 * @param {string} endDM   - 'DD.MM'
 * @param {Date} now
 * @returns {boolean}
 */
function inRecurringWindow(startDM, endDM, now) {
	const s = dmToNumber(startDM);
	const e = dmToNumber(endDM);
	const cur = ((now.getMonth() + 1) * 100) + now.getDate();

	if (e >= s) {
		return cur >= s && cur <= e;
	}
	// перетин року (наприклад, 01.12–10.01)
	return cur >= s || cur <= e;
}

/*
 * Перевіряє, чи now входить у одноразове вікно YMD..YMD (включно).
 * @param {string|Date} startYmd
 * @param {string|Date} endYmd
 * @param {Date} now
 * @returns {boolean}
 */
function inOneOffWindow(startYmd, endYmd, now) {
	const start = (startYmd instanceof Date) ? startYmd : new Date(startYmd);
	const end = (endYmd instanceof Date) ? endYmd : new Date(endYmd);

	const n = now.getTime();
	return n >= start.getTime() && n <= end.getTime();
}

/*
 * Чи входить now у діапазон, враховуючи тип дат (DD.MM vs YYYY-MM-DD).
 * @param {Pick<DateWindow,'start'|'end'|'recurring'>} w
 * @param {Date=} now
 * @returns {boolean}
 */
export function isNowInWindow(w, now = new Date()) {
	const { start, end } = w;

	if (isDM(String(start)) && isDM(String(end))) {
		return inRecurringWindow(String(start), String(end), now);
	}

	if ((isYMD(String(start)) || start instanceof Date) &&
			(isYMD(String(end)) || end instanceof Date)) {
		return inOneOffWindow(start, end, now);
	}

	// Якщо формат не розпізнано — вважаємо, що вікно не активне
	return false;
}

/*
 * Повертає key першого активного вікна або null.
 * Порядок у масиві важливий (перший збіг — переможець).
 *
 * @param {DateWindow[]} windows
 * @param {Date=} now
 * @returns {string|null}
 */
export function pickActiveKey(windows, now = new Date()) {
	for (let i = 0; i < windows.length; i += 1) {
		const w = windows[i];
		if (isNowInWindow(w, now)) {
			return w.key;
		}
	}
	return null;
}
